// "Add to Calendar" links + .ics for a booked appointment (Jose 2026-10-29).
// Times are FLOATING local wall-clock (no timezone conversion) so the client's calendar shows the exact
// time they booked on any device — and the classic `new Date('yyyy-mm-dd')` UTC-shift can't happen because
// the start Date is built with the LOCAL (y,m,d,h,m) constructor by the caller (see parseLocalStart).
export interface CalEvent {
  title: string;
  start: Date;          // local wall-clock instant (built with new Date(y, m-1, d, h, min))
  durationMin: number;
  location?: string;
  details?: string;
  tz?: string;          // IANA for Google's ctz; default America/New_York
}

const pad = (n: number) => String(n).padStart(2, '0');
const floatLocal = (d: Date) =>
  `${d.getFullYear()}${pad(d.getMonth() + 1)}${pad(d.getDate())}T${pad(d.getHours())}${pad(d.getMinutes())}00`;
const isoLocal = (d: Date) =>
  `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}:00`;
const endOf = (e: CalEvent) => new Date(e.start.getTime() + Math.max(15, e.durationMin || 30) * 60000);

/** Build a LOCAL Date from a "YYYY-MM-DD" + a "3:00 PM" / "15:00" time — no UTC shift. */
export function parseLocalStart(ymd: string, time: string): Date {
  const [y, m, d] = ymd.split('-').map(Number);
  const ap = /(am|pm)/i.exec(time);
  const [hRaw, mRaw] = time.replace(/\s*(am|pm)/i, '').trim().split(':');
  let h = Number(hRaw); const min = Number(mRaw || 0);
  if (ap) { const pm = /pm/i.test(ap[0]); if (pm && h !== 12) h += 12; if (!pm && h === 12) h = 0; }
  return new Date(y, (m || 1) - 1, d || 1, h || 0, min || 0, 0, 0);
}

export function googleCalUrl(e: CalEvent): string {
  const p = new URLSearchParams({ action: 'TEMPLATE', text: e.title,
    dates: `${floatLocal(e.start)}/${floatLocal(endOf(e))}`, ctz: e.tz || 'America/New_York' });
  if (e.details) p.set('details', e.details);
  if (e.location) p.set('location', e.location);
  return `https://calendar.google.com/calendar/render?${p.toString()}`;
}

export function outlookUrl(e: CalEvent): string {
  const p = new URLSearchParams({ path: '/calendar/action/compose', rru: 'addevent', subject: e.title,
    startdt: isoLocal(e.start), enddt: isoLocal(endOf(e)) });
  if (e.details) p.set('body', e.details);
  if (e.location) p.set('location', e.location);
  return `https://outlook.live.com/calendar/0/deeplink/compose?${p.toString()}`;
}

export function yahooUrl(e: CalEvent): string {
  const dur = Math.max(15, e.durationMin || 30);
  const p = new URLSearchParams({ v: '60', title: e.title, st: floatLocal(e.start),
    dur: `${pad(Math.floor(dur / 60))}${pad(dur % 60)}` });
  if (e.details) p.set('desc', e.details);
  if (e.location) p.set('in_loc', e.location);
  return `https://calendar.yahoo.com/?${p.toString()}`;
}

/** iCalendar text (Apple Calendar, Outlook desktop, any .ics-aware app). Floating local time. */
export function icsString(e: CalEvent): string {
  const esc = (s = '') => s.replace(/\\/g, '\\\\').replace(/;/g, '\\;').replace(/,/g, '\\,').replace(/\n/g, '\\n');
  const uid = `${floatLocal(e.start)}-${Math.random().toString(36).slice(2)}@slikbarberlounge.com`;
  return [
    'BEGIN:VCALENDAR', 'VERSION:2.0', 'PRODID:-//SLIK Barber Lounge//Booking//EN', 'CALSCALE:GREGORIAN', 'METHOD:PUBLISH',
    'BEGIN:VEVENT', `UID:${uid}`, `DTSTAMP:${floatLocal(new Date())}`,
    `DTSTART:${floatLocal(e.start)}`, `DTEND:${floatLocal(endOf(e))}`,
    `SUMMARY:${esc(e.title)}`,
    e.location ? `LOCATION:${esc(e.location)}` : '',
    e.details ? `DESCRIPTION:${esc(e.details)}` : '',
    'BEGIN:VALARM', 'TRIGGER:-PT2H', 'ACTION:DISPLAY', `DESCRIPTION:${esc(e.title)}`, 'END:VALARM',
    'END:VEVENT', 'END:VCALENDAR',
  ].filter(Boolean).join('\r\n');
}

/** Download the .ics (opens Apple Calendar on iOS/Mac; imports into Outlook desktop, etc.). */
export function downloadIcs(e: CalEvent, filename = 'slik-appointment.ics') {
  const blob = new Blob([icsString(e)], { type: 'text/calendar;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url; a.download = filename;
  document.body.appendChild(a); a.click(); document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(url), 1500);
}
